/**
 * Centralised image-CDN helper for the Zeely-Mirror surfaces.
 *
 * Thumbnails are self-hosted under `/zeely-mirror/<id>.webp` in the
 * frontend's public dir. The same WebP file (~800×1000 @ q=82) serves
 * every aspect ratio — the CSS `object-cover` on each card crops to
 * fit. Self-hosting avoids the external network dependency on Unsplash
 * (their CDN occasionally retires photo IDs, breaking our cards) and
 * gives us a single asset surface to swap when we ship Banan-generated
 * brand-tailored thumbnails (Phase 13+).
 *
 * The catalog files (`exploreToolsCatalog`, `referenceLibraryCatalog`,
 * etc.) keep using the same Unsplash-derived ID slugs so the visual
 * curation history is preserved; only the URL output of this helper
 * changes. Files were sourced from Unsplash under their Unsplash
 * License (commercial use, no attribution required).
 *
 * The `ThumbDimensions` argument is kept for backward compatibility —
 * the self-hosted file is one size and CSS handles the rest, so the
 * dim param is ignored at the URL level but still threaded through
 * the call sites so a future return to a sizing CDN is a one-line
 * change.
 */

import { cdnUrl } from "@/lib/utils";

export type ThumbDimensions = {
  /** Pixel width hint (kept for callsite compatibility — currently
   *  unused because we serve a single size and CSS-crop). */
  w: number;
  /** Pixel height hint. */
  h: number;
};

/**
 * Compose a self-hosted Zeely-Mirror thumbnail URL.
 *
 * Files live in `frontend/public/zeely-mirror/<id>.webp` and the
 * Vite/nginx static handler serves them from `/zeely-mirror/<id>.webp`.
 *
 * @param id  Unsplash-style photo ID slug (preserved from curation
 *            history; the actual file is local).
 * @param _dim Dim hint, currently unused (see file comment).
 */
export function unsplash(id: string, _dim: ThumbDimensions): string {
  return cdnUrl(`/zeely-mirror/${id}.webp`);
}

/** Standard aspect-ratio presets used across the Zeely-Mirror cards. */
export const THUMB = {
  /** Top tool cards on landing (`16:10` landscape). */
  topTool: { w: 800, h: 500 } satisfies ThumbDimensions,
  /** UGC preset cards + Inspiration cards (`3:4` portrait). */
  card34: { w: 600, h: 800 } satisfies ThumbDimensions,
  /** Builder-side preset thumb inside UGC builder (`16:10`). */
  builderPreset: { w: 600, h: 375 } satisfies ThumbDimensions,
};
